<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";

import { computePosition } from "@/dsComponents/Dropdown/helpers";
import { type ControlledDropdownProps, DropdownDirections, type supporttedEventNames } from "@/dsComponents/Dropdown/types";

import { ADJUST_POSITION_DELAY } from ".";

const props = withDefaults(defineProps<ControlledDropdownProps>(), {
    nostyle: false,
    height: "auto",
    direction: DropdownDirections.AUTOMATIC,
    maxWidth: "800px",
    maxHeight: "500px",
    fitContent: false,
});

const emit = defineEmits<(event: "change", eventName: supporttedEventNames) => void>();

const cancelLeaveEvent = ref();
const handleMouseEvent = (eventName: supporttedEventNames) => {
    if (eventName === "mouseenter") {
        if (cancelLeaveEvent.value) clearTimeout(cancelLeaveEvent.value);
        emit("change", eventName);
        return;
    }
    if (eventName === "mouseleave") {
        if (cancelLeaveEvent.value) clearTimeout(cancelLeaveEvent.value);
        cancelLeaveEvent.value = setTimeout(() => emit("change", eventName), 120);
        return;
    }
    emit("change", eventName);
};

const customId = computed(() => {
    const id = Math.round(Math.random() * 1000);
    return `controlled-dropdown-${String(id).padStart(3, "0")}`;
});

const dropdown = ref();
const clickArea = ref<HTMLDivElement>();
const dropdownArea = ref<HTMLDivElement>();
const positionLeft = ref(0);
const positionTop = ref(0);
const minWidth = ref("200px");

const adjustPosition = async () => {
    const position = await computePosition(clickArea.value, dropdownArea.value, props.direction);
    if (position) {
        positionLeft.value = position.left;
        positionTop.value = position.top;
    }
};

const isDescendant = (event: Event) => {
    const selector = `#ds-dropdowns .${customId.value}`;
    return event.target?.matches(`${selector}, ${selector} *`);
};

const handleClickAway = (event: MouseEvent) => {
    if (dropdown.value && props.open) {
        const triggeredBy = `.trigger.${customId.value}`;
        const isTriggeredBy = event.target?.matches(`${triggeredBy}, ${triggeredBy} *`);
        const descend = isDescendant(event);

        if (descend) emit("change", "descendant-click");

        if (!descend && !isTriggeredBy) {
            handleMouseEvent("clickaway");
        }
    }
};

const handleDropdownClose = () => {
    if (props.open) {
        emit("change", "clickaway");
    }
};

const timestamp = ref(0);
const handleScrollOrResize = (event: Event) => {
    if (!props.open) return;
    if (isDescendant(event)) return;
    // Adjust position every ADJUST_POSITION_DELAY milliseconds
    if (performance.now() - timestamp.value < ADJUST_POSITION_DELAY) return;
    timestamp.value = performance.now();
    setTimeout(async () => {
        await adjustPosition();
    }, ADJUST_POSITION_DELAY);
};

onMounted(() => {
    document.addEventListener("click", handleClickAway);
    document.addEventListener("dropdown-close", handleDropdownClose);
    window.addEventListener("resize", handleScrollOrResize);
    document.addEventListener("scroll", handleScrollOrResize, true);
});

onUnmounted(() => {
    document.removeEventListener("click", handleClickAway);
    document.removeEventListener("dropdown-close", handleDropdownClose);
    window.removeEventListener("resize", handleScrollOrResize);
    document.removeEventListener("scroll", handleScrollOrResize, true);
});

watch(
    () => props.open,
    async () => {
        if (props.open) {
            await adjustPosition();
        }
    },
    { immediate: true }
);
</script>
<template>
    <div ref="dropdown" :class="['ds-controlled-dropdown-component', { 'ds-controlled-dropdown-component--full-width': fullWidth }]">
        <div
            ref="clickArea"
            :class="['trigger', customId, { fitContent: fitContent && !fullWidth }]"
            @click="() => handleMouseEvent('click')"
            @mouseenter="() => handleMouseEvent('mouseenter')"
            @mouseleave="() => handleMouseEvent('mouseleave')"
        >
            <slot name="trigger-zone"></slot>
        </div>
        <Teleport to="#ds-dropdowns">
            <div
                :data-debug="`top: ${positionTop}px, left: ${positionLeft}px`"
                :class="['ControlledDropdown', customId, direction, { nostyle }]"
                :id="customId"
                :style="{
                    '--dropdown-position-y': `${positionTop}px`,
                    '--dropdown-position-x': `${positionLeft}px`,
                    'min-width': minWidth,
                    'max-width': maxWidth,
                }"
                ref="dropdownArea"
                v-show="open"
                @mouseenter="() => handleMouseEvent('mouseenter')"
                @mouseleave="() => handleMouseEvent('mouseleave')"
            >
                <div class="dropdown-content" :style="{ maxHeight, maxWidth, minWidth, height, ...(styles ?? {}) }">
                    <slot name="dropdown-content"></slot>
                </div>
            </div>
        </Teleport>
    </div>
</template>
<style scoped lang="scss">
.ControlledDropdown {
    width: fit-content;
    position: absolute;
    z-index: 99999;

    @include use-dropdown-animations;
    @include regular-shadow-md;
    top: var(--dropdown-position-y);
    left: var(--dropdown-position-x);

    &:not(.nostyle) {
        .dropdown-content {
            border: 1px solid var(--stroke-soft-200, #dbdfe6);
            padding: var(--spacing-md, 8px);
            border-radius: var(--radius-md, 8px);
            background-color: var(--bg-white-0, #ffffff);
            height: 100%;
            max-height: 100%;
            overflow: auto;
            display: flex;
            flex-direction: column;
            gap: var(--spacing-xs, 4px);
        }
    }

    .dropdown-content {
        height: fit-content;
    }
}

.ds-controlled-dropdown-component {
    height: 100%;
    position: relative;
    cursor: pointer;
    .trigger {
        height: 100%;
        &.fitContent {
            width: fit-content;
        }
    }

    &--full-width {
        width: 100%;
        .trigger {
            width: 100%;
        }
    }
}
</style>
